const state = {
  items: [],
  filtered: [],
  summary: null,
  renderVersion: 0,
  sortDescending: false,
  viewMode: "list",
  timelineZoom: "year",
  timelineBasis: "availability",
  pendingManufacturer: "",
  pendingCore: "",
};

const refs = {
  searchInput: document.getElementById("searchInput"),
  categoryOptions: document.querySelectorAll("[data-category-option]"),
  generationControl: document.getElementById("generationControl"),
  generationSelect: document.getElementById("generationSelect"),
  memoryControl: document.getElementById("memoryControl"),
  memorySelect: document.getElementById("memorySelect"),
  manufacturerControl: document.getElementById("manufacturerControl"),
  manufacturerSelect: document.getElementById("manufacturerSelect"),
  coreControl: document.getElementById("coreControl"),
  coreSelect: document.getElementById("coreSelect"),
  sortSelect: document.getElementById("sortSelect"),
  sortDirectionToggle: document.getElementById("sortDirectionToggle"),
  viewToggle: document.getElementById("viewToggle"),
  viewOptions: document.querySelectorAll("[data-view-mode]"),
  timelineZoomControl: document.getElementById("timelineZoomControl"),
  timelineZoomSelect: document.getElementById("timelineZoomSelect"),
  timelineBasisControl: document.getElementById("timelineBasisControl"),
  timelineBasisSelect: document.getElementById("timelineBasisSelect"),
  arcadeFilterGroup: document.getElementById("arcadeFilterGroup"),
  mainArcadeToggle: document.getElementById("mainArcadeToggle"),
  alternativeArcadeToggle: document.getElementById("alternativeArcadeToggle"),
  releaseFilterGroup: document.getElementById("releaseFilterGroup"),
  releasedToggle: document.getElementById("releasedToggle"),
  prereleaseToggle: document.getElementById("prereleaseToggle"),
  resultCount: document.getElementById("resultCount"),
  updatedAt: document.getElementById("updatedAt"),
  todayPanel: document.getElementById("todayPanel"),
  todaySummaryMeta: document.getElementById("todaySummaryMeta"),
  todayDateLabel: document.getElementById("todayDateLabel"),
  todayList: document.getElementById("todayList"),
  heroStats: document.getElementById("heroStats"),
  statusBanner: document.getElementById("statusBanner"),
  coreGrid: document.getElementById("coreGrid"),
  cardTemplate: document.getElementById("cardTemplate"),
};

const numberFormatter = new Intl.NumberFormat("en-US");
const textCollator = new Intl.Collator("en-US", {
  numeric: true,
  sensitivity: "base",
});
const dateFormatter = new Intl.DateTimeFormat("en-US", {
  dateStyle: "medium",
  timeStyle: "short",
});
const timelineEntryDateFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: "UTC",
  month: "short",
  day: "numeric",
});
const timelineEntryDateWithYearFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: "UTC",
  month: "short",
  day: "numeric",
  year: "numeric",
});
const timelineMonthFormatter = new Intl.DateTimeFormat("en-US", {
  timeZone: "UTC",
  month: "long",
  year: "numeric",
});
const todayDateFormatter = new Intl.DateTimeFormat("en-US", {
  month: "long",
  day: "numeric",
});
const DEFAULT_CATALOG_DATA_URL = "./data/cores.json";

function normalizeOptionalUrl(value) {
  return String(value || "").trim();
}

function getConfiguredCatalogDataUrl() {
  const globalCatalogUrl = normalizeOptionalUrl(globalThis.MISTER_CORE_CATALOG_URL);
  if (globalCatalogUrl) return globalCatalogUrl;

  const appShell = document.querySelector(".mister-core-explorer-app");
  return normalizeOptionalUrl(appShell && appShell.dataset ? appShell.dataset.catalogUrl : "");
}

function getCatalogDataUrl() {
  return getConfiguredCatalogDataUrl() || DEFAULT_CATALOG_DATA_URL;
}

async function fetchCatalogPayload(url) {
  const response = await fetch(url, { cache: "no-store" });
  if (!response.ok) {
    throw new Error(`Request failed with ${response.status}`);
  }
  return response.json();
}
const PLATFORM_VARIANT_FAMILIES = [
  {
    id: "gameboy-family",
    displayName: "Game Boy",
    primaryName: "Gameboy",
    memberNames: ["Gameboy", "Gameboy2P"],
  },
  {
    id: "gba-family",
    displayName: "Game Boy Advance",
    primaryName: "GBA",
    memberNames: ["GBA", "GBA2P"],
  },
];
const PLATFORM_SEARCH_ALIAS_MAP = {
  NES: ["Famicom", "Nintendo Entertainment System"],
  SNES: ["Super Famicom", "Super Nintendo", "Super Nintendo Entertainment System"],
  Genesis: ["MegaDrive", "Mega Drive", "Sega Mega Drive", "Sega Genesis"],
  MegaDrive: ["Genesis", "Mega Drive", "Sega Mega Drive", "Sega Genesis"],
  SMS: ["Master System", "Sega Master System"],
  TurboGrafx16: ["PC Engine", "TurboGrafx-16"],
};
const TODAY_EVENT_FIELDS = [
  { field: "originDate", label: "Core/game" },
  { field: "firstPatreonDate", label: "Patreon" },
  { field: "firstPublicDate", label: "Available" },
  { field: "releaseDate", label: "Package" },
];
const CATEGORY_VALUES = ["Arcade", "Console", "Handheld", "Computer", "Others", "Utilities"];

function showStatus(message) {
  refs.statusBanner.textContent = message;
  refs.statusBanner.hidden = false;
}

function hideStatus() {
  refs.statusBanner.hidden = true;
  refs.statusBanner.textContent = "";
}

function normalizeShellLayout() {
  const todaySection = refs.todayPanel && typeof refs.todayPanel.closest === "function"
    ? refs.todayPanel.closest(".today-section")
    : document.querySelector(".today-section");
  const controlsPanel = refs.searchInput && typeof refs.searchInput.closest === "function"
    ? refs.searchInput.closest(".controls-panel")
    : document.querySelector(".controls-panel");
  const parent = todaySection && controlsPanel && todaySection.parentNode === controlsPanel.parentNode
    ? todaySection.parentNode
    : null;

  if (parent) {
    const siblings = Array.from(parent.children || []);
    if (siblings.indexOf(todaySection) > siblings.indexOf(controlsPanel)) {
      parent.insertBefore(todaySection, controlsPanel);
    }
  }

  if (refs.statusBanner && String(refs.statusBanner.textContent || "").trim() === "Loading catalog data…") {
    hideStatus();
  }
}

function syncSortDirectionToggle() {
  refs.sortDirectionToggle.textContent = state.sortDescending ? "Descending" : "Ascending";
  refs.sortDirectionToggle.setAttribute("aria-pressed", String(state.sortDescending));
  refs.sortDirectionToggle.title = state.sortDescending
    ? "Switch to ascending sort"
    : "Switch to descending sort";
}

function syncViewToggle() {
  refs.viewOptions.forEach((button) => {
    const active = button.dataset.viewMode === state.viewMode;
    button.classList.toggle("is-active", active);
    button.setAttribute("aria-pressed", String(active));
  });
}

function syncTimelineZoomControl() {
  const isTimeline = state.viewMode === "timeline";
  refs.timelineZoomControl.classList.toggle("is-hidden", !isTimeline);
  refs.timelineZoomSelect.value = state.timelineZoom;
  refs.timelineBasisControl.classList.toggle("is-hidden", !isTimeline);
  refs.timelineBasisSelect.value = state.timelineBasis;
}

function formatCount(value, label) {
  return `${numberFormatter.format(value)} ${label}`;
}

function normalizeCategorySelection(value) {
  const values = Array.isArray(value) ? value : value ? [value] : [];
  return values.filter((category, index) => CATEGORY_VALUES.includes(category) && values.indexOf(category) === index);
}

function categorySelectionIncludes(selectedCategories, category) {
  const normalized = normalizeCategorySelection(selectedCategories);
  return !normalized.length || normalized.includes(category);
}

function categorySelectionIsOnly(selectedCategories, category) {
  const normalized = normalizeCategorySelection(selectedCategories);
  return normalized.length === 1 && normalized[0] === category;
}

function getSelectedCategories() {
  const options = Array.from(refs.categoryOptions || []);
  if (!options.length) return [];

  const selected = options.filter((option) => option.checked).map((option) => option.value);
  if (!selected.length || selected.length === options.length) return [];
  return normalizeCategorySelection(selected);
}

function setSelectedCategories(value) {
  const selected = normalizeCategorySelection(value);
  const showAll = !selected.length;
  Array.from(refs.categoryOptions || []).forEach((option) => {
    option.checked = showAll || selected.includes(option.value);
  });
}

function itemMatchesCategoryFilter(item, selectedCategories) {
  const normalized = normalizeCategorySelection(selectedCategories);
  return !normalized.length || normalized.includes(item.category);
}

function categorySupportsAlternatives(selectedCategories) {
  return categorySelectionIncludes(selectedCategories, "Arcade");
}

function categorySupportsGenerations(selectedCategories) {
  return categorySelectionIsOnly(selectedCategories, "Console");
}

function categorySupportsMemoryFilter(selectedCategories) {
  return true;
}

function itemMatchesMemoryFilter(item, selectedMemory) {
  if (!selectedMemory) return true;
  if (selectedMemory === "dual") return Boolean(item.dualSdramEnhanced);
  if (selectedMemory === "single") return !item.dualSdramEnhanced;
  return true;
}

function categorySupportsCoreFilter(selectedCategories) {
  return categorySelectionIncludes(selectedCategories, "Arcade");
}

function categorySupportsReleaseFilter(selectedCategories) {
  return true;
}

function syncArcadeFilterGroup(selectedCategories) {
  refs.arcadeFilterGroup.classList.toggle("is-hidden", !categorySupportsAlternatives(selectedCategories));
}

function syncGenerationControl(selectedCategories) {
  refs.generationControl.classList.toggle("is-hidden", !categorySupportsGenerations(selectedCategories));
}

function syncMemoryControl(selectedCategories) {
  refs.memoryControl.classList.toggle("is-hidden", !categorySupportsMemoryFilter(selectedCategories));
}

function syncReleaseFilterGroup(selectedCategories) {
  refs.releaseFilterGroup.classList.toggle("is-hidden", !categorySupportsReleaseFilter(selectedCategories));
}

function getCoreLabel(item) {
  return item.type === "arcade" ? item.coreName || "" : "";
}

function getAuthorLabel(item) {
  return item.coreAuthor || "";
}

function getManufacturerLabel(item) {
  return item.manufacturerGroup || item.manufacturer || "";
}

function isHttpUrl(value) {
  return /^https?:\/\//i.test(String(value || ""));
}

function getUrlHostLabel(value) {
  if (!isHttpUrl(value)) return "";
  try {
    return new URL(value).hostname.replace(/^www\./i, "");
  } catch (error) {
    return "";
  }
}

function githubRepoRootUrl(value) {
  if (!isHttpUrl(value)) return "";
  try {
    const url = new URL(value);
    if (!["github.com", "www.github.com"].includes(url.hostname.toLowerCase())) return "";
    const parts = url.pathname.split("/").filter(Boolean);
    if (parts.length < 2) return "";
    return `https://github.com/${parts[0]}/${parts[1]}`;
  } catch (error) {
    return "";
  }
}

function getNewsSourceUrl(item) {
  for (const mention of item.newsMentions || []) {
    for (const url of mention.sourceUrls || []) {
      if (isHttpUrl(url) && !String(url).includes("patreon.com/")) {
        return url;
      }
    }
  }
  return isHttpUrl(item.sourceUrl) ? item.sourceUrl : item.newsUrl || "";
}

function getPublicSourceUrl(item) {
  return item.publicSourceUrl || githubRepoRootUrl(item.sourceUrl) || githubRepoRootUrl(getNewsSourceUrl(item)) || item.sourceUrl || "";
}

function getItemSourceKind(item) {
  if (item.sourceAvailability === "open" || item.hasPublicSource === true) return "open";
  if (item.sourceAvailability === "closed" || item.hasPublicSource === false) return "closed";
  if (item.sourceKey === "mister-devel" || item.sourceKey === "jotego-patreon") return "open";
  if (item.sourceKey === "coin-op-collection") {
    return String(item.sourceUrl || "").includes("github.com/va7deo/") ? "open" : "closed";
  }
  if (item.sourceKey === "external-github-mra") {
    return "closed";
  }
  return "";
}

function getSourceVisibility(item) {
  const sourceKind = getItemSourceKind(item);
  if (!sourceKind) return null;

  const publicSourceUrl = getPublicSourceUrl(item);
  const sourceLabel = getUrlHostLabel(publicSourceUrl);
  const suffix = sourceLabel ? ` (${sourceLabel})` : "";
  const isOpenSource = sourceKind === "open";
  let title = item.sourceAvailabilityNote || "";
  if (!title) {
    title = isOpenSource
      ? `Public source code is available${suffix}`
      : "No public HDL source repository is referenced by the catalog";
  }
  if (isOpenSource && publicSourceUrl) {
    title = `Open public source code${suffix}${item.sourceAvailabilityNote ? `\n${item.sourceAvailabilityNote}` : ""}`;
  }

  return {
    label: isOpenSource ? "Open source" : "Closed source",
    search: isOpenSource ? "open source" : "closed source",
    kind: isOpenSource ? "source-open" : "source-closed",
    href: isOpenSource ? publicSourceUrl : "",
    title,
  };
}

function getSourceLinkTitle(item) {
  const host = getUrlHostLabel(item.sourceUrl);
  const suffix = host ? ` (${host})` : "";
  if (item.sourceKey === "external-github-mra") {
    return getItemSourceKind(item) === "open"
      ? `Open public source code for this external GitHub MRA${suffix}`
      : `Open linked release/reference for this external GitHub MRA${suffix}; no public source code link found`;
  }
  const lastChange = item.lastSourceChangeDate
    ? `\nLast change: ${item.lastSourceChangeDate}`
    : "";
  if (String(item.sourceUrl || "").includes("github.com/")) {
    return `Open ${item.source} on GitHub${lastChange}`;
  }
  return `Open ${item.source}${suffix}${lastChange}`;
}

function isMisterDevelItem(item) {
  return item.sourceKey === "mister-devel";
}

function itemMatchesReleaseFilter(item, includeReleased, includePrerelease) {
  if (item.isPrerelease && !includePrerelease) return false;
  if (!item.isPrerelease && !includeReleased) return false;
  return true;
}

function itemMatchesArcadeVariantFilter(item, includeMain, includeAlternative) {
  if (item.type !== "arcade") return true;
  if (item.isAlternative && !includeAlternative) return false;
  if (!item.isAlternative && !includeMain) return false;
  return true;
}

function buildRuntimeSummary(items) {
  const categories = {};
  const sources = {};
  let totalArcade = 0;
  let arcadeAlternatives = 0;

  for (const item of items) {
    categories[item.category] = (categories[item.category] || 0) + 1;
    sources[item.source] = (sources[item.source] || 0) + 1;
    if (item.type === "arcade") {
      totalArcade += 1;
      if (item.isAlternative) arcadeAlternatives += 1;
    }
  }

  return {
    totalItems: items.length,
    totalArcade,
    totalPlatforms: items.length - totalArcade,
    arcadeAlternatives,
    categories,
    sources,
  };
}

function mergePlatformVariantFamilies(items) {
  const familyByMember = new Map();
  for (const family of PLATFORM_VARIANT_FAMILIES) {
    for (const name of family.memberNames) {
      familyByMember.set(name.toLowerCase(), family);
    }
  }

  const groupedVariants = new Map();
  for (const item of items) {
    if (item.type !== "platform") continue;
    const family = familyByMember.get(String(item.name || "").toLowerCase());
    if (!family) continue;
    const variants = groupedVariants.get(family.id) || [];
    variants.push(item);
    groupedVariants.set(family.id, variants);
  }

  const mergedByFamily = new Map();
  for (const family of PLATFORM_VARIANT_FAMILIES) {
    const variants = groupedVariants.get(family.id);
    if (!variants || variants.length < 2) continue;

    const primary = variants.find((item) => item.name === family.primaryName) || variants[0];
    const aliases = Array.from(
      new Set(
        variants.flatMap((item) => [
          item.name,
          item.shortName,
          item.coreName,
        ]).filter(Boolean),
      ),
    );

    mergedByFamily.set(family.id, {
      ...primary,
      name: family.displayName,
      shortName: family.displayName,
      coreName: family.displayName,
      variantNames: aliases,
      searchAliases: aliases,
      coreAuthor: primary.coreAuthor || variants.map((item) => item.coreAuthor).find(Boolean) || "",
    });
  }

  const emittedFamilies = new Set();
  const mergedItems = [];
  for (const item of items) {
    if (item.type !== "platform") {
      mergedItems.push(item);
      continue;
    }

    const family = familyByMember.get(String(item.name || "").toLowerCase());
    if (!family) {
      mergedItems.push(item);
      continue;
    }

    const merged = mergedByFamily.get(family.id);
    if (!merged) {
      mergedItems.push(item);
      continue;
    }

    if (emittedFamilies.has(family.id)) continue;
    emittedFamilies.add(family.id);
    mergedItems.push(merged);
  }

  return mergedItems;
}

function attachPlatformSearchAliases(items) {
  for (const item of items) {
    if (item.type !== "platform") continue;

    const aliasParts = [
      ...(Array.isArray(item.searchAliases) ? item.searchAliases : []),
      ...(PLATFORM_SEARCH_ALIAS_MAP[item.name] || []),
      ...(PLATFORM_SEARCH_ALIAS_MAP[item.shortName] || []),
      ...(PLATFORM_SEARCH_ALIAS_MAP[item.coreName] || []),
    ];
    const aliases = Array.from(new Set(aliasParts.filter(Boolean)));
    if (aliases.length) {
      item.searchAliases = aliases;
    }
  }
}

function parseCatalogDate(value) {
  if (!value) return null;
  const timestamp = Date.parse(`${value}T00:00:00Z`);
  return Number.isNaN(timestamp) ? null : timestamp;
}

function minCatalogDateString(...values) {
  const valid = values.filter((value) => parseCatalogDate(value) !== null);
  if (!valid.length) return "";
  return valid.sort((left, right) => parseCatalogDate(left) - parseCatalogDate(right))[0];
}

function localMonthDay(date) {
  return [
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
}

function catalogMonthDay(value) {
  return parseCatalogDate(value) === null ? "" : String(value).slice(5, 10);
}

function getTodayInMisterEvents(items, today = new Date()) {
  const targetMonthDay = localMonthDay(today);
  const events = [];

  for (const item of items) {
    const labelsByDate = new Map();
    for (const eventField of TODAY_EVENT_FIELDS) {
      const dateString = item[eventField.field] || "";
      if (catalogMonthDay(dateString) !== targetMonthDay) continue;
      if (!labelsByDate.has(dateString)) {
        labelsByDate.set(dateString, []);
      }
      const labels = labelsByDate.get(dateString);
      if (!labels.includes(eventField.label)) {
        labels.push(eventField.label);
      }
    }

    for (const [date, labels] of labelsByDate.entries()) {
      events.push({ item, date, labels });
    }
  }

  return events.sort(
    (left, right) =>
      parseCatalogDate(left.date) - parseCatalogDate(right.date) ||
      textCollator.compare(left.item.category || "", right.item.category || "") ||
      textCollator.compare(left.item.name || "", right.item.name || ""),
  );
}

function normalizeSearchIndex(parts) {
  return parts
    .flatMap((part) => (Array.isArray(part) ? part : [part]))
    .filter(Boolean)
    .map((part) => String(part).toLowerCase())
    .join(" ");
}

function buildBaseSearchIndex(item) {
  const parts = [
    item.name,
    item.shortName,
    item.searchAliases || [],
    item.variantNames || [],
    item.setname,
    item.source,
    item.category,
    item.subcategory,
    getManufacturerLabel(item),
    item.manufacturer,
    getAuthorLabel(item),
    item.generation,
    item.year,
    item.releaseDate,
    item.lastSourceChangeDate,
    item.firstNewsDate,
    (item.newsMentions || []).flatMap((mention) => [
      mention.sectionTitle,
      mention.postTitle,
      mention.postDate,
    ]),
    item.hardwareReleaseYear,
    item.hardwareAgeYears,
    getSourceVisibility(item)?.search || "",
    item.coreName,
    item.romZips || [],
  ];

  return normalizeSearchIndex(parts);
}

function primeSearchIndexes(items) {
  for (const item of items) {
    item.baseSearchIndex = buildBaseSearchIndex(item);
  }
}

function itemMatchesQuery(item, query) {
  if (!query) return true;
  return item.baseSearchIndex.includes(query);
}

function getMiSTerReleaseValue(item) {
  const firstAvailability = minCatalogDateString(
    item.firstPatreonDate,
    item.firstPublicDate,
    item.releaseDate,
  );
  return parseCatalogDate(firstAvailability) ?? parseCatalogDate(item.originDate) ?? parseCatalogDate(item.lastSourceChangeDate);
}

function getOriginalReleaseValue(item) {
  if (Number.isFinite(item.hardwareReleaseYear)) return item.hardwareReleaseYear;
  const year = Number.parseInt(item.year, 10);
  return Number.isNaN(year) ? null : year;
}

function getTimelineDateString(item) {
  if (state.timelineBasis === "availability") {
    if (item.sourceKey === "mister-devel") {
      return item.originDate || item.firstPublicDate || item.releaseDate || "";
    }
    return minCatalogDateString(
      item.firstPatreonDate,
      item.firstPublicDate,
      item.releaseDate,
    ) || item.originDate || "";
  }
  if (state.timelineBasis === "package") {
    return item.releaseDate || item.firstPublicDate || item.firstPatreonDate || item.originDate || "";
  }
  return item.originDate || item.firstPublicDate || item.firstPatreonDate || item.releaseDate || "";
}

function getTimelineDateValue(item) {
  return parseCatalogDate(getTimelineDateString(item));
}

function getConsoleGenerationValue(item) {
  if (item.category !== "Console") return null;
  return Number.isFinite(item.generationOrder) ? item.generationOrder : Number.MAX_SAFE_INTEGER;
}

function compareByName(left, right) {
  return (
    textCollator.compare(left.name, right.name) ||
    textCollator.compare(left.shortName || "", right.shortName || "") ||
    textCollator.compare(left.source || "", right.source || "")
  );
}

function compareItemsBySelectedSort(left, right) {
  const mode = refs.sortSelect.value;
  const direction = state.sortDescending ? -1 : 1;

  if (mode === "console-generation") {
    const leftValue = getConsoleGenerationValue(left);
    const rightValue = getConsoleGenerationValue(right);
    if (leftValue !== null || rightValue !== null) {
      if (leftValue === null) return 1;
      if (rightValue === null) return -1;
      if (leftValue !== rightValue) return (leftValue - rightValue) * direction;
      if ((left.generation || "") !== (right.generation || "")) {
        return textCollator.compare(left.generation || "", right.generation || "") * direction;
      }
    }
  } else if (mode === "mister-release") {
    const leftValue = getMiSTerReleaseValue(left);
    const rightValue = getMiSTerReleaseValue(right);
    if (leftValue !== null || rightValue !== null) {
      if (leftValue === null) return 1;
      if (rightValue === null) return -1;
      if (leftValue !== rightValue) return (leftValue - rightValue) * direction;
    }
  } else if (mode === "original-release") {
    const leftValue = getOriginalReleaseValue(left);
    const rightValue = getOriginalReleaseValue(right);
    if (leftValue !== null || rightValue !== null) {
      if (leftValue === null) return 1;
      if (rightValue === null) return -1;
      if (leftValue !== rightValue) return (leftValue - rightValue) * direction;
    }
  }

  return compareByName(left, right) * direction;
}

function sortFilteredItems(items) {
  items.sort(compareItemsBySelectedSort);

  return items;
}

function categoryClassName(category) {
  return category
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function populateGenerationOptions(items) {
  const generations = Array.from(
    new Map(
      items
        .filter((item) => item.category === "Console" && item.generation)
        .sort(
          (left, right) =>
            (left.generationOrder ?? Number.MAX_SAFE_INTEGER) -
              (right.generationOrder ?? Number.MAX_SAFE_INTEGER) ||
            left.generation.localeCompare(right.generation),
        )
        .map((item) => [item.generation, item.generation]),
    ).values(),
  );

  refs.generationSelect.innerHTML = "";

  const allOption = document.createElement("option");
  allOption.value = "";
  allOption.textContent = "All generations";
  refs.generationSelect.appendChild(allOption);

  for (const generation of generations) {
    const option = document.createElement("option");
    option.value = generation;
    option.textContent = generation;
    refs.generationSelect.appendChild(option);
  }
}

function populateManufacturerOptions(items, preferredValue = refs.manufacturerSelect.value) {
  const manufacturers = Array.from(
    items.reduce((counts, item) => {
      const label = getManufacturerLabel(item);
      if (!label) return counts;
      counts.set(label, (counts.get(label) || 0) + 1);
      return counts;
    }, new Map()).entries(),
  )
    .map(([label, count]) => ({ label, count }))
    .sort((left, right) => left.label.localeCompare(right.label));

  refs.manufacturerSelect.innerHTML = "";

  const allOption = document.createElement("option");
  allOption.value = "";
  allOption.textContent = "All";
  refs.manufacturerSelect.appendChild(allOption);

  for (const manufacturer of manufacturers) {
    const option = document.createElement("option");
    option.value = manufacturer.label;
    option.textContent = `${manufacturer.label} (${numberFormatter.format(manufacturer.count)})`;
    refs.manufacturerSelect.appendChild(option);
  }

  refs.manufacturerControl.classList.toggle("is-hidden", manufacturers.length === 0);
  refs.manufacturerSelect.value = manufacturers.some((item) => item.label === preferredValue)
    ? preferredValue
    : "";
}

function populateCoreOptions(items, preferredValue = refs.coreSelect.value, isRelevant = true) {
  const cores = Array.from(
    items.reduce((counts, item) => {
      const label = getCoreLabel(item);
      if (!label) return counts;
      counts.set(label, (counts.get(label) || 0) + 1);
      return counts;
    }, new Map()).entries(),
  )
    .map(([label, count]) => ({ label, count }))
    .sort((left, right) => textCollator.compare(left.label, right.label));

  refs.coreSelect.innerHTML = "";

  const allOption = document.createElement("option");
  allOption.value = "";
  allOption.textContent = "All";
  refs.coreSelect.appendChild(allOption);

  for (const core of cores) {
    const option = document.createElement("option");
    option.value = core.label;
    option.textContent = `${core.label} (${numberFormatter.format(core.count)})`;
    refs.coreSelect.appendChild(option);
  }

  refs.coreControl.classList.toggle("is-hidden", !isRelevant || cores.length === 0);
  refs.coreSelect.value = cores.some((item) => item.label === preferredValue)
    ? preferredValue
    : "";
}

function applyQuickFilter(filter) {
  if ("category" in filter) setSelectedCategories(filter.category);
  if ("search" in filter) refs.searchInput.value = filter.search;
  if ("generation" in filter) refs.generationSelect.value = filter.generation;
  if ("manufacturer" in filter) {
    state.pendingManufacturer = filter.manufacturer;
    refs.manufacturerSelect.value = "";
  }
  if ("core" in filter) {
    state.pendingCore = filter.core;
    refs.coreSelect.value = "";
  }
  if ("showAlternatives" in filter) refs.alternativeArcadeToggle.checked = filter.showAlternatives;

  if ("releaseKind" in filter) {
    refs.releasedToggle.checked = filter.releaseKind !== "pre-release";
    refs.prereleaseToggle.checked = filter.releaseKind !== "released";
  }
  if ("arcadeVariant" in filter) {
    refs.mainArcadeToggle.checked = filter.arcadeVariant !== "alternative";
    refs.alternativeArcadeToggle.checked = filter.arcadeVariant !== "main";
  }
  applyFilters();
}

function syncHeroStatSelection(selectedCategories) {
  const normalized = normalizeCategorySelection(selectedCategories);
  for (const stat of refs.heroStats.querySelectorAll("[data-category-filter]")) {
    const category = stat.dataset.categoryFilter || "";
    const isActive = category ? normalized.includes(category) : !normalized.length;
    stat.classList.toggle("is-active", isActive);
    stat.setAttribute("aria-pressed", String(isActive));
  }
}

function buildHeroStats(summary) {
  const statPairs = [
    { label: "Total entries", value: summary.totalItems, category: "" },
    { label: "Arcade", value: summary.categories["Arcade"] || 0, category: "Arcade" },
    { label: "Console", value: summary.categories["Console"] || 0, category: "Console" },
    { label: "Handheld", value: summary.categories["Handheld"] || 0, category: "Handheld" },
    { label: "Computer", value: summary.categories["Computer"] || 0, category: "Computer" },
    { label: "Others", value: summary.categories["Others"] || 0, category: "Others" },
    { label: "Utilities", value: summary.categories["Utilities"] || 0, category: "Utilities" },
  ];

  refs.heroStats.innerHTML = "";
  for (const { label, value, category } of statPairs) {
    const chip = document.createElement("button");
    chip.type = "button";
    chip.className = `hero-stat category-${categoryClassName(category || "All")}`;
    chip.dataset.categoryFilter = category;
    chip.setAttribute("aria-pressed", "false");
    chip.title = category ? `Filter to ${label}` : "Show all categories";
    chip.innerHTML = `<strong>${numberFormatter.format(value)}</strong><span>${label}</span>`;
    refs.heroStats.appendChild(chip);
  }
}

function buildTags(item) {
  const tags = [];
  const manufacturerLabel = getManufacturerLabel(item);
  const showAlternatives = item.category === "Arcade" && item.isAlternative ? true : refs.alternativeArcadeToggle.checked;
  const sourceVisibility = getSourceVisibility(item);
  const isJotegoPatreon = item.sourceKey === "jotego-patreon";

  if (sourceVisibility) {
    const tag = {
      label: sourceVisibility.label,
      kind: sourceVisibility.kind,
      title: sourceVisibility.title,
    };
    if (sourceVisibility.href) {
      tag.href = sourceVisibility.href;
    } else {
      tag.filter = {
        category: "",
        search: sourceVisibility.search,
        generation: "",
        manufacturer: "",
        core: "",
        showAlternatives,
      };
    }
    tags.push(tag);
  }

  if (isMisterDevelItem(item)) {
    tags.push({
      label: "MiSTer-devel",
      kind: "provenance-mister-devel",
      title: "Hosted by the MiSTer-devel GitHub organization",
    });
  }

  if (item.type === "platform" && item.dualSdramEnhanced) {
    tags.push({
      label: "Dual SDRAM Enhanced",
      kind: "dual-sdram",
      href: item.dualSdramUrl,
      title: `Open the Dual SDRAM enhanced build for ${item.name}${item.dualSdramReleaseDate ? ` (${item.dualSdramReleaseDate})` : ""}`,
    });
  }

  if (item.coreName && item.coreName !== item.name) {
    if (item.category === "Arcade") {
      tags.push({
        label: `Core: ${item.coreName}`,
        filter: {
          category: "Arcade",
          search: "",
          generation: "",
          manufacturer: "",
          core: item.coreName,
          showAlternatives,
        },
        title: `Filter arcade results to ${item.coreName}`,
      });
    } else {
      tags.push({
        label: `Core: ${item.coreName}`,
        filter: {
          category: item.category,
          search: item.coreName,
          generation: "",
          manufacturer: "",
          core: "",
          showAlternatives,
        },
        title: `Filter to ${item.coreName}`,
      });
    }
  }
  if (item.type === "platform" && item.hardwareReleaseYear) {
    tags.push({
      label: String(item.hardwareReleaseYear),
      title: `Original hardware release year for ${item.name}`,
    });
  }
  if (item.type === "platform" && item.category === "Console" && item.generation) {
    tags.push({
      label: item.generation,
      filter: {
        category: "Console",
        search: "",
        generation: item.generation,
        manufacturer: "",
        core: "",
      },
      title: `Filter to ${item.generation}`,
    });
  }
  if (item.manufacturer && manufacturerLabel) {
    tags.push({
      label: `Mfg: ${manufacturerLabel}`,
      filter: {
        category: item.category,
        search: "",
        generation: "",
        manufacturer: manufacturerLabel,
        core: "",
        showAlternatives,
      },
      title: `Filter to ${manufacturerLabel}`,
    });
  }
  if (getAuthorLabel(item)) {
    const authorLabel = getAuthorLabel(item);
    const isMultiAuthor = authorLabel === "Multi";
    tags.push({
      label: `Author: ${authorLabel}`,
      filter: {
        category: "",
        search: authorLabel,
        generation: "",
        manufacturer: "",
        core: "",
        showAlternatives,
      },
      title: isMultiAuthor
        ? "Filter to cores with multiple major contributors"
        : `Filter to cores by ${authorLabel}`,
    });
  }
  if (item.originDate) {
    tags.push({
      label: isJotegoPatreon ? `Core since: ${item.originDate}` : item.releaseChannel === "Patreon" ? `Released: ${item.originDate}` : `Origin: ${item.originDate}`,
      title: isJotegoPatreon
        ? `Earliest known Jotego core-family introduction date for ${item.coreName || item.name}`
        : item.releaseChannel === "Patreon"
          ? `Earliest known Patreon release date for the ${item.coreName || item.name} core`
          : `Earliest known release date for the ${item.coreName || item.name} core`,
    });
  }
  if (isJotegoPatreon && item.firstPatreonDate && item.firstPatreonDate !== item.originDate) {
    tags.push({
      label: `Patreon: ${item.firstPatreonDate}`,
      title: `Earliest known Patreon post with release files for ${item.name}`,
    });
  }
  if (item.firstPublicDate && (item.sourceKey !== "mister-devel" || item.firstPublicDate !== item.originDate)) {
    tags.push({
      label: `Public: ${item.firstPublicDate}`,
      title: `Earliest known public release date for the ${item.coreName || item.name} core`,
    });
  }
  if (item.type === "platform" && item.releaseDate) {
    tags.push({
      label: `Official: ${item.releaseDate}`,
      href: item.publicReleaseUrl || item.distributionUrl || item.sourceUrl,
      title: `Open the official public build packaged in Distribution_MiSTer for ${item.name}`,
    });
  }
  if (item.type === "platform" && Number.isFinite(item.commitsSincePublicRelease)) {
    tags.push({
      label: `${item.commitsSincePublicRelease} changes since official`,
      href: item.commitsSincePublicReleaseUrl || item.historyUrl || item.sourceUrl,
      title: `Open upstream changes since the official public release of ${item.name}`,
    });
  }
  if (item.type === "platform" && item.unstableReleaseDate) {
    tags.push({
      label: "Download unstable",
      href: item.unstableReleaseUrl || item.upstreamReleasesUrl || item.sourceUrl,
      download: true,
      title: `Download the latest unstable RBF for ${item.name}${item.unstableReleaseDate ? ` (${item.unstableReleaseDate})` : ""}`,
    });
  }
  if (item.patreonUrl) {
    tags.push({
      label: "Patreon",
      href: item.patreonUrl,
      title: `Open ${item.source} on Patreon`,
    });
  }
  if (item.lastSourceChangeDate && item.type !== "platform") {
    const updatedTagTitle = item.historyUrl
      ? `Open upstream history for ${item.name}`
      : `Most recent source change on ${item.lastSourceChangeDate}`;
    tags.push({
      label: `Source updated: ${item.lastSourceChangeDate}`,
      href: item.historyUrl || item.sourceUrl,
      title: updatedTagTitle,
    });
  }
  if (item.isPrerelease) {
    tags.push({
      label: "Pre-release",
      kind: "prerelease",
      title: item.prereleaseNote || "Pre-release / alpha / experimental core",
    });
  }
  if (item.isArchived) {
    tags.push({
      label: "Archived",
      kind: "archived",
      title: item.archivedNote || "Upstream repo archived",
    });
  }
  if (item.year) {
    tags.push({
      label: String(item.year),
      title: `Original arcade release year for ${item.name}`,
    });
  }
  if (item.type !== "platform" && !item.lastSourceChangeDate && item.releaseDate) {
    const label = item.releaseChannel === "Patreon"
      ? `Package: ${item.releaseDate}`
      : `Public: ${item.releaseDate}`;
    const title = item.releaseChannel === "Patreon"
      ? `Dated third-party package represented in the catalog for ${item.name}`
      : `Public catalog release date for ${item.name}`;
    if (!tags.some((tag) => tag.label === label)) {
      tags.push({ label, title });
    }
  }
  if (item.isAlternative) tags.push({ label: "Alternative", kind: "arcade-alternative" });
  return tags.slice(0, item.type === "platform" ? 10 : 10);
}

function buildSubtitle(item) {
  if (item.type === "arcade") {
    const parts = [
      item.manufacturer,
      item.shortName ? `Set ${item.shortName}` : "",
    ].filter(Boolean);
    return parts.join(" • ");
  }

  return getManufacturerLabel(item);
}

function buildCard(item) {
  const fragment = refs.cardTemplate.content.cloneNode(true);
  const article = fragment.querySelector(".core-card");
  const category = fragment.querySelector(".card-category");
  const title = fragment.querySelector(".card-title");
  const subtitle = fragment.querySelector(".card-subtitle");
  const tags = fragment.querySelector(".card-tags");

  article.classList.add(`category-${categoryClassName(item.category)}`);
  article.classList.add(`type-${item.type}`);
  if (item.isAlternative) article.classList.add("is-alternative");

  category.textContent = item.category;
  category.dataset.filter = JSON.stringify({
    category: item.category,
    search: "",
    generation: "",
    manufacturer: "",
    core: "",
    showAlternatives: item.category === "Arcade" && item.isAlternative
      ? true
      : refs.alternativeArcadeToggle.checked,
  });
  category.title = `Filter to ${item.category}`;
  title.textContent = "";
  const titleLink = document.createElement(item.sourceUrl ? "a" : "span");
  titleLink.className = "card-title-link";
  titleLink.textContent = item.name;
  if (item.sourceUrl) {
    titleLink.href = item.sourceUrl;
    titleLink.target = "_blank";
    titleLink.rel = "noreferrer noopener";
    titleLink.title = getSourceLinkTitle(item);
  }
  title.append(titleLink);
  if (item.isPaywalled) {
    const lock = document.createElement("span");
    lock.className = "title-lock";
    lock.textContent = "🔒";
    lock.title = item.paywallNote || "Supporter-locked core";
    lock.setAttribute("aria-label", item.paywallKind === "beta" ? "Beta-locked" : "Paywalled");
    title.append(lock);
  }
  const subtitleText = buildSubtitle(item);
  subtitle.textContent = subtitleText;
  subtitle.hidden = !subtitleText;

  for (const tagData of buildTags(item)) {
    let tag;
    if (tagData.href) {
      tag = document.createElement("a");
    } else if (tagData.filter) {
      tag = document.createElement("button");
      tag.type = "button";
      tag.classList.add("tag-filter");
      tag.dataset.filter = JSON.stringify(tagData.filter);
    } else {
      tag = document.createElement("span");
    }
    tag.textContent = tagData.label;
    if (tagData.kind) {
      tag.classList.add(`tag-${tagData.kind}`);
    }
    if (tagData.href) {
      tag.href = tagData.href;
      tag.target = "_blank";
      tag.rel = "noreferrer noopener";
      tag.classList.add("tag-link");
      if (tagData.download) {
        tag.setAttribute("download", "");
      }
    }
    if (tagData.title) {
      tag.title = tagData.title;
    }
    tags.appendChild(tag);
  }

  return fragment;
}

function buildTodayEvent(event) {
  const item = event.item;
  const row = document.createElement("article");
  row.className = `today-event category-${categoryClassName(item.category)}`;

  const dateBlock = document.createElement("div");
  dateBlock.className = "today-event-date";

  const year = document.createElement("span");
  year.className = "today-event-year";
  year.textContent = String(event.date).slice(0, 4);

  const kind = document.createElement("span");
  kind.className = "today-event-kind";
  kind.textContent = event.labels.join(" + ");

  const main = document.createElement("div");
  main.className = "today-event-main";

  const title = document.createElement(item.sourceUrl ? "a" : "span");
  title.className = "today-event-title";
  title.textContent = item.name;
  if (item.sourceUrl) {
    title.href = item.sourceUrl;
    title.target = "_blank";
    title.rel = "noreferrer noopener";
    title.title = getSourceLinkTitle(item);
  }

  const meta = document.createElement("p");
  meta.className = "today-event-meta";
  meta.textContent = [
    item.category,
    getManufacturerLabel(item),
    item.type === "arcade" && item.coreName ? `Core ${item.coreName}` : "",
  ].filter(Boolean).join(" / ");

  dateBlock.append(year, kind);
  main.append(title, meta);
  row.append(dateBlock, main);
  return row;
}

function renderTodayInMister(items, today = new Date()) {
  const events = getTodayInMisterEvents(items, today);
  const dateLabel = todayDateFormatter.format(today);

  refs.todayDateLabel.textContent = `${dateLabel} across all catalog years`;
  refs.todaySummaryMeta.textContent = events.length
    ? formatCount(events.length, events.length === 1 ? "release" : "releases")
    : `No releases for ${dateLabel}`;
  refs.todayPanel.open = events.length > 0;
  refs.todayList.innerHTML = "";

  if (!events.length) {
    const empty = document.createElement("p");
    empty.className = "today-empty";
    empty.textContent = "No release dates in the catalog match this month and day.";
    refs.todayList.appendChild(empty);
    return;
  }

  const fragment = document.createDocumentFragment();
  for (const event of events) {
    fragment.appendChild(buildTodayEvent(event));
  }
  refs.todayList.appendChild(fragment);
}

function buildGenerationGroups(items) {
  const groups = new Map();

  for (const item of items) {
    const label = item.generation || "Unknown / uncategorized";
    if (!groups.has(label)) {
      groups.set(label, {
        label,
        order: item.generationOrder ?? Number.MAX_SAFE_INTEGER,
        items: [],
      });
    }
    groups.get(label).items.push(item);
  }

  return Array.from(groups.values()).sort(
    (left, right) => left.order - right.order || left.label.localeCompare(right.label),
  );
}

function buildGenerationSection(group) {
  const section = document.createElement("section");
  section.className = "generation-group";

  const header = document.createElement("div");
  header.className = "generation-group-header";

  const title = document.createElement("h2");
  title.className = "generation-group-title";
  title.textContent = group.label;

  const count = document.createElement("p");
  count.className = "generation-group-count";
  count.textContent = formatCount(group.items.length, group.items.length === 1 ? "core" : "cores");

  const grid = document.createElement("div");
  grid.className = "generation-group-grid";

  const fragment = document.createDocumentFragment();
  for (const item of group.items) {
    fragment.appendChild(buildCard(item));
  }

  header.append(title, count);
  grid.appendChild(fragment);
  section.append(header, grid);
  return section;
}

function formatTimelineGroup(dateString, zoom) {
  if (!dateString) {
    return {
      key: "undated",
      label: "Undated",
      sortValue: Number.POSITIVE_INFINITY,
    };
  }

  const date = new Date(`${dateString}T00:00:00Z`);
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth();

  if (zoom === "month") {
    return {
      key: `${year}-${String(month + 1).padStart(2, "0")}`,
      label: timelineMonthFormatter.format(date),
      sortValue: Date.UTC(year, month, 1),
    };
  }

  if (zoom === "quarter") {
    const quarter = Math.floor(month / 3) + 1;
    return {
      key: `${year}-q${quarter}`,
      label: `${year} Q${quarter}`,
      sortValue: Date.UTC(year, (quarter - 1) * 3, 1),
    };
  }

  return {
    key: String(year),
    label: String(year),
    sortValue: Date.UTC(year, 0, 1),
  };
}

function formatTimelineEntryDate(dateString, zoom) {
  if (!dateString) return "Date unknown";

  const date = new Date(`${dateString}T00:00:00Z`);
  if (zoom === "year" || zoom === "quarter" || zoom === "month") {
    return timelineEntryDateFormatter.format(date);
  }
  return timelineEntryDateWithYearFormatter.format(date);
}

function compareTimelineItems(left, right) {
  const leftValue = getTimelineDateValue(left);
  const rightValue = getTimelineDateValue(right);
  const direction = state.sortDescending ? -1 : 1;

  if (leftValue !== null || rightValue !== null) {
    if (leftValue === null) return 1;
    if (rightValue === null) return -1;
    if (leftValue !== rightValue) return (leftValue - rightValue) * direction;
  }

  return compareItemsBySelectedSort(left, right);
}

function buildTimelineGroups(items) {
  const zoom = state.timelineZoom;
  const sortedItems = [...items].sort(compareTimelineItems);
  const groups = new Map();

  for (const item of sortedItems) {
    const dateString = getTimelineDateString(item);
    const groupInfo = formatTimelineGroup(dateString, zoom);
    if (!groups.has(groupInfo.key)) {
      groups.set(groupInfo.key, {
        key: groupInfo.key,
        label: groupInfo.label,
        sortValue: groupInfo.sortValue,
        items: [],
      });
    }
    groups.get(groupInfo.key).items.push(item);
  }

  return Array.from(groups.values());
}

function buildTimelineEntry(item) {
  const entry = document.createElement("article");
  entry.className = `timeline-entry category-${categoryClassName(item.category)}`;

  const dateBlock = document.createElement("div");
  dateBlock.className = "timeline-entry-date";

  const dateLabel = document.createElement("span");
  dateLabel.className = "timeline-entry-date-label";
  dateLabel.textContent = formatTimelineEntryDate(getTimelineDateString(item), state.timelineZoom);

  const marker = document.createElement("span");
  marker.className = "timeline-entry-marker";
  marker.setAttribute("aria-hidden", "true");

  const cardWrap = document.createElement("div");
  cardWrap.className = "timeline-entry-card";
  cardWrap.appendChild(buildCard(item));

  dateBlock.append(dateLabel, marker);
  entry.append(dateBlock, cardWrap);
  return entry;
}

function buildTimelineSection(group) {
  const section = document.createElement("section");
  section.className = "timeline-group";

  const header = document.createElement("div");
  header.className = "timeline-group-header";

  const title = document.createElement("h2");
  title.className = "timeline-group-title";
  title.textContent = group.label;

  const count = document.createElement("p");
  count.className = "timeline-group-count";
  count.textContent = formatCount(group.items.length, group.items.length === 1 ? "entry" : "entries");

  const items = document.createElement("div");
  items.className = "timeline-group-items";

  const fragment = document.createDocumentFragment();
  for (const item of group.items) {
    fragment.appendChild(buildTimelineEntry(item));
  }

  header.append(title, count);
  items.appendChild(fragment);
  section.append(header, items);
  return section;
}

function renderTimelineItems(items) {
  const groups = buildTimelineGroups(items);
  const fragment = document.createDocumentFragment();

  for (const group of groups) {
    fragment.appendChild(buildTimelineSection(group));
  }

  refs.coreGrid.appendChild(fragment);
}

function renderItems(items, options = {}) {
  const renderVersion = ++state.renderVersion;
  refs.coreGrid.innerHTML = "";
  refs.coreGrid.classList.remove("is-grouped", "view-list", "view-timeline");

  if (!items.length) {
    const empty = document.createElement("div");
    empty.className = "empty-state";
    empty.textContent = "No entries match the current search and filter combination.";
    refs.coreGrid.appendChild(empty);
    return;
  }

  if (state.viewMode === "timeline") {
    refs.coreGrid.classList.add("view-list", "view-timeline");
    renderTimelineItems(items);
    return;
  }

  refs.coreGrid.classList.add("view-list");

  const batchSize = 120;
  let index = 0;

  function renderBatch() {
    if (renderVersion !== state.renderVersion) return;

    const fragment = document.createDocumentFragment();
    for (const item of items.slice(index, index + batchSize)) {
      fragment.appendChild(buildCard(item));
    }

    refs.coreGrid.appendChild(fragment);
    index += batchSize;

    if (index < items.length) {
      window.setTimeout(renderBatch, 0);
      return;
    }
  }

  window.setTimeout(renderBatch, 0);
}

function applyFilters() {
  const query = refs.searchInput.value.trim().toLowerCase();
  const selectedCategories = getSelectedCategories();
  const selectedGeneration = refs.generationSelect.value;
  const selectedMemory = refs.memorySelect.value;
  const arcadeVariantRelevant = categorySupportsAlternatives(selectedCategories);
  const generationsRelevant = categorySupportsGenerations(selectedCategories);
  const memoryRelevant = categorySupportsMemoryFilter(selectedCategories);
  const coreRelevant = categorySupportsCoreFilter(selectedCategories);
  const releaseRelevant = categorySupportsReleaseFilter(selectedCategories);
  const includeMainArcade = !arcadeVariantRelevant || refs.mainArcadeToggle.checked;
  const includeAlternativeArcade = !arcadeVariantRelevant || refs.alternativeArcadeToggle.checked;
  const includeReleased = !releaseRelevant || refs.releasedToggle.checked;
  const includePrerelease = !releaseRelevant || refs.prereleaseToggle.checked;
  const preferredManufacturer = state.pendingManufacturer || refs.manufacturerSelect.value;
  const preferredCore = state.pendingCore || refs.coreSelect.value;
  state.pendingManufacturer = "";
  state.pendingCore = "";

  syncHeroStatSelection(selectedCategories);
  syncGenerationControl(selectedCategories);
  syncMemoryControl(selectedCategories);
  syncArcadeFilterGroup(selectedCategories);
  syncReleaseFilterGroup(selectedCategories);
  syncViewToggle();
  syncTimelineZoomControl();
  const baseItems = state.items.filter((item) => {
    if (!itemMatchesCategoryFilter(item, selectedCategories)) return false;
    if (generationsRelevant && selectedGeneration && item.generation !== selectedGeneration) return false;
    if (memoryRelevant && !itemMatchesMemoryFilter(item, selectedMemory)) return false;
    if (!itemMatchesArcadeVariantFilter(item, includeMainArcade, includeAlternativeArcade)) return false;
    if (!itemMatchesReleaseFilter(item, includeReleased, includePrerelease)) return false;
    return true;
  });
  populateManufacturerOptions(
    baseItems.filter((item) => !coreRelevant || !preferredCore || getCoreLabel(item) === preferredCore),
    preferredManufacturer,
  );

  const selectedManufacturer = refs.manufacturerSelect.value;
  populateCoreOptions(
    baseItems.filter((item) => {
      if (item.type !== "arcade") return false;
      if (selectedManufacturer && getManufacturerLabel(item) !== selectedManufacturer) return false;
      return true;
    }),
    preferredCore,
    coreRelevant,
  );
  const selectedCore = refs.coreSelect.value;

  state.filtered = state.items.filter((item) => {
    if (!itemMatchesCategoryFilter(item, selectedCategories)) return false;
    if (generationsRelevant && selectedGeneration && item.generation !== selectedGeneration) return false;
    if (memoryRelevant && !itemMatchesMemoryFilter(item, selectedMemory)) return false;
    if (!itemMatchesArcadeVariantFilter(item, includeMainArcade, includeAlternativeArcade)) return false;
    if (!itemMatchesReleaseFilter(item, includeReleased, includePrerelease)) return false;
    if (selectedManufacturer && getManufacturerLabel(item) !== selectedManufacturer) return false;
    if (coreRelevant && selectedCore && getCoreLabel(item) !== selectedCore) return false;
    if (!itemMatchesQuery(item, query)) return false;
    return true;
  });
  sortFilteredItems(state.filtered);

  refs.resultCount.textContent = formatCount(state.filtered.length, state.filtered.length === 1 ? "result" : "results");
  hideStatus();
  renderItems(state.filtered);
}

async function loadCatalog() {
  try {
    const inlineCatalog = globalThis.MISTER_CORE_CATALOG;
    let payload = null;

    if (inlineCatalog) {
      payload = inlineCatalog;
    } else {
      try {
        payload = await fetchCatalogPayload(getCatalogDataUrl());
      } catch (error) {
        throw error;
      }
    }
    state.items = mergePlatformVariantFamilies(payload.items || []);
    attachPlatformSearchAliases(state.items);
    state.summary = buildRuntimeSummary(state.items);
    primeSearchIndexes(state.items);

    buildHeroStats(state.summary);
    renderTodayInMister(state.items);
    populateGenerationOptions(state.items);
    refs.updatedAt.textContent = payload.generatedAt
      ? dateFormatter.format(new Date(payload.generatedAt))
      : "Unknown";
    syncSortDirectionToggle();
    syncViewToggle();
    syncTimelineZoomControl();

    applyFilters();
  } catch (error) {
    console.error(error);
    refs.resultCount.textContent = "Unavailable";
    refs.updatedAt.textContent = "Unavailable";
    showStatus("Catalog data could not be loaded.");
    refs.coreGrid.innerHTML = '<div class="empty-state">The catalog JSON could not be loaded. Build the data file and refresh the page.</div>';
  }
}

refs.searchInput.addEventListener("input", applyFilters);
Array.from(refs.categoryOptions || []).forEach((option) => {
  option.addEventListener("change", () => {
    if (!Array.from(refs.categoryOptions || []).some((candidate) => candidate.checked)) {
      setSelectedCategories([]);
    }
    applyFilters();
  });
});
refs.generationSelect.addEventListener("change", applyFilters);
refs.memorySelect.addEventListener("change", applyFilters);
refs.manufacturerSelect.addEventListener("change", applyFilters);
refs.coreSelect.addEventListener("change", applyFilters);
refs.sortSelect.addEventListener("change", applyFilters);
refs.sortDirectionToggle.addEventListener("click", () => {
  state.sortDescending = !state.sortDescending;
  syncSortDirectionToggle();
  applyFilters();
});
refs.viewOptions.forEach((button) => {
  button.addEventListener("click", () => {
    const nextMode = button.dataset.viewMode || "list";
    if (state.viewMode === nextMode) return;
    state.viewMode = nextMode;
    syncViewToggle();
    syncTimelineZoomControl();
    renderItems(state.filtered);
  });
});
refs.timelineZoomSelect.addEventListener("change", () => {
  state.timelineZoom = refs.timelineZoomSelect.value || "year";
  syncTimelineZoomControl();
  if (state.viewMode === "timeline") {
    renderItems(state.filtered);
  }
});
refs.timelineBasisSelect.addEventListener("change", () => {
  state.timelineBasis = refs.timelineBasisSelect.value || "availability";
  syncTimelineZoomControl();
  if (state.viewMode === "timeline") {
    renderItems(state.filtered);
  }
});
refs.mainArcadeToggle.addEventListener("change", applyFilters);
refs.alternativeArcadeToggle.addEventListener("change", applyFilters);
refs.releasedToggle.addEventListener("change", applyFilters);
refs.prereleaseToggle.addEventListener("change", applyFilters);
refs.coreGrid.addEventListener("click", (event) => {
  const button = event.target.closest("[data-filter]");
  if (!button) return;

  try {
    applyQuickFilter(JSON.parse(button.dataset.filter));
  } catch (error) {
    console.error(error);
  }
});
refs.heroStats.addEventListener("click", (event) => {
  const button = event.target.closest("[data-category-filter]");
  if (!button) return;

  setSelectedCategories(button.dataset.categoryFilter || "");
  applyFilters();
});
normalizeShellLayout();
syncSortDirectionToggle();
syncViewToggle();
syncTimelineZoomControl();
loadCatalog();
